import { isNil } from 'lodash';
import { rootWinPath, windowsUpperCaseInvalidPath } from 'mbs-ui-shared@constants';
import { ISOFormatWithSec, MaxDate, MinDate } from 'mbs-ui-shared@constants/c-sharp-constants';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { DefaultSelectData } from 'mbs-ui-shared@models/SelectData';
import { baseDateFormatWithSeconds, cobbledDateFormat, onlyDateFormat } from 'mbs-ui-shared@utils/date';
import moment from 'moment';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { SupportMethodsForStepsFromPlan } from 'wizards/helpers/support-methods-for-steps-from-plan';
import { EC2DataValue, EC2StepValue } from 'wizards/models/EC2-models';
import { AzureDataValue, AzureStepValue } from 'wizards/models/azure-models';
import { BackupToRestoreStepValue, cbfStringMap, IdsForCurrentFormats } from 'wizards/models/backup-to-restore-models';
import { DeletePlan } from 'wizards/models/base/base-models/plan-restore-delete-plan-model';
import { RestorePlanType } from 'wizards/models/base/base-models/plan-restore-plan-type-model';
import { DefaultTemplate, DefaultTemplateMask, DestinationDBStepValue } from 'wizards/models/destination-db-models';
import { DestinationStepValue } from 'wizards/models/destination-models';
import { DestinationVMStepValue } from 'wizards/models/destination-vm-models';
import { EncryptionOptionsStepValue } from 'wizards/models/encryption-options-models';
import { PartitionsStepValue } from 'wizards/models/partitions-models';
import { PlanNameStepValue } from 'wizards/models/plan-name-models';
import { RestoreDisksStepValue } from 'wizards/models/restore-disks-models';
import { RestorePointStepValue, RestoreType, SpecificDateGroup } from 'wizards/models/restore-point-models';
import { HostTypeNumbers } from 'wizards/models/select-host-models';
import { VirtualDisksSelectedType } from 'wizards/models/select-virtual-disks-models';
import { TemporaryDataValue, TemporaryInstanceStepValue } from 'wizards/models/temporary-instance-models';
import {
  AzureRestoreTypeEnum,
  EC2RestoreTypeEnum,
  RestoreAsDiskEnum,
  RestoreTestTypeToRestoreAs,
  RestoreTypeFromAgentEnum,
  TypeDataStepValue,
  VirtualDiskFileFormat
} from 'wizards/models/type-data-models';
import { DiskVolumeView } from 'wizards/models/what-backup-models';
import { RestoreSourceStepValue } from '../../models/restore-source-models';

export class RestoreStepsDataFromPlanHelper {
  public static updatePlanNameStep(planName: string, deletePlan: DeletePlan): PlanNameStepValue {
    return {
      Name: planName || '',
      SavePlanInCloud: deletePlan === DeletePlan.Never,
      valid: true
    };
  }

  public static updateFileFormats(formatsList: VirtualDiskFileFormat[]): string[] {
    return formatsList ? formatsList.map((item) => item.DisplayName).sort((a: string, b: string) => (a.length < b.length ? 1 : -1)) : [];
  }

  public static updateTypeDataStep(plan, planType: PlanTypes = null): TypeDataStepValue {
    const typeData: TypeDataStepValue = {
      restoreAs:
        plan.DiskImageRestoreType === RestoreTypeFromAgentEnum.VirtualDisk ? RestoreAsDiskEnum.Virtual : RestoreAsDiskEnum.Physical,
      createAMI: false,
      selectedFileFormat: plan.DiskImageFileFormat ? plan.DiskImageFileFormat.DisplayName : '',
      valid: true
    };

    if (plan.TestVmSettings) {
      typeData.restoreAs = RestoreTestTypeToRestoreAs[plan.TestVmSettings.TestType] ?? RestoreAsDiskEnum.InstantRestoreToHyperV;

      return typeData;
    }

    if (planType === PlanTypes.RestoreDiskImagePlan && plan.DiskImageRestoreType === RestoreTypeFromAgentEnum.RestoreToVMware) {
      typeData.restoreAs = RestoreAsDiskEnum.RestoreToVMwareVSphere;

      return typeData;
    }

    if (planType === PlanTypes.RestoreDiskImagePlan && plan.DiskImageRestoreType === RestoreTypeFromAgentEnum.RestoreToHyperV) {
      typeData.restoreAs = RestoreAsDiskEnum.RestoreToHyperV;

      return typeData;
    }

    if (planType === PlanTypes.RestoreDiskImagePlan && plan.DiskImageRestoreType === RestoreTypeFromAgentEnum.RestoreToProxmox) {
      typeData.restoreAs = RestoreAsDiskEnum.RestoreToProxmox;

      return typeData;
    }

    if (planType === PlanTypes.RestoreProxmox) {
      typeData.restoreAs = RestoreAsDiskEnum.RestoreToProxmox;

      return typeData;
    }

    if (plan.RestorePlanType === RestorePlanType.VMToCloud)
      typeData.selectedFileFormat = plan.RestoreToVirtualDisk?.DiskImageFileFormat?.DisplayName || '';

    if (planType === PlanTypes.RestoreVMware || planType === PlanTypes.RestoreVMwareToCustom) {
      typeData.restoreAs =
        plan.RestorePlanType === RestorePlanType.VMwareMachine ? RestoreAsDiskEnum.RestoreToVMware : RestoreAsDiskEnum.Virtual;

      return typeData;
    }

    if (planType === PlanTypes.RestoreHyperV || planType === PlanTypes.RestoreHyperVToCustom) {
      typeData.restoreAs =
        plan.RestorePlanType === RestorePlanType.HyperV
          ? plan?.ImportVm
            ? RestoreAsDiskEnum.RestoreToHyperVAndImport
            : RestoreAsDiskEnum.RestoreToHyperV
          : RestoreAsDiskEnum.Virtual;

      return typeData;
    }

    if (plan.DiskImageRestoreType === RestoreTypeFromAgentEnum.RestoreToAzure) {
      typeData.restoreAs =
        plan.RestoreToAzure?.AzureRestoreType === AzureRestoreTypeEnum.AzureInstance ? RestoreAsDiskEnum.AVM : RestoreAsDiskEnum.ADD;

      return typeData;
    }

    if (plan.DiskImageRestoreType !== RestoreTypeFromAgentEnum.RestoreToEC2) return typeData;

    this.updateTypeDataEC2(plan.RestoreToEC2.EC2RestoreType, typeData);

    return typeData;
  }

  public static updateAzureStep(plan): AzureStepValue {
    return {
      account: plan.RestoreToAzure.AzureVMConnectionID,
      computerName: plan.RestoreToAzure.InstanceName,
      dataDiskName: plan.RestoreToAzure.InstanceName,
      locations: plan.RestoreToAzure.Location,
      resourceGroup: plan.RestoreToAzure.ResourceGroupName,
      vmSize: plan.RestoreToAzure.VMSize,
      network: plan.RestoreToAzure.NetworkName,
      subnet: plan.RestoreToAzure.SubnetName,
      storage: plan.RestoreToAzure.StorageName,
      container: plan.RestoreToAzure.BucketName,
      bootDiagnosticStorage: plan.RestoreToAzure.BootDiagnosticStorageName,
      valid: true
    };
  }

  public static updateEC2(plan): EC2StepValue {
    return {
      account: plan.RestoreToEC2.EC2ConnectionId,
      region: plan.RestoreToEC2.RegionName,
      availabilityZones: plan.RestoreToEC2.AvailabilityZone,
      instanceType: plan.RestoreToEC2.InstanceType || 't1_micro',
      subnet: plan.RestoreToEC2.SubnetId,
      securityGroup: plan.RestoreToEC2.SecurityGroupId,
      valid: true
    };
  }

  public static updateTemporaryInstance(plan): TemporaryInstanceStepValue {
    return {
      useTemporary: plan.RestoreToEC2TemporaryInstance.IsRemoteRestore,
      account: plan.RestoreToEC2TemporaryInstance.EC2ConnectionId,
      region: plan.RestoreToEC2TemporaryInstance.RegionName,
      availabilityZones: plan.RestoreToEC2TemporaryInstance.AvailabilityZone,
      instanceType: plan.RestoreToEC2TemporaryInstance.InstanceType || 't1_micro',
      subnet: plan.RestoreToEC2TemporaryInstance.SubnetId,
      securityGroup: plan.RestoreToEC2TemporaryInstance.SecurityGroupId,
      ami: plan.RestoreToEC2TemporaryInstance.AMI,
      valid: true
    };
  }

  // TODO NEED ADD Azure EC2 Temporary Instance data
  public static updateAzureData(plan, currentData: AzureDataValue): AzureDataValue {
    return currentData;
  }

  public static updateEC2Data(plan, currentData: EC2DataValue): EC2DataValue {
    return currentData;
  }

  public static updateTemporaryData(plan, currentData: TemporaryDataValue): TemporaryDataValue {
    return currentData;
  }

  public static updatePartitions(diskInfo: any[]): DefaultSelectData[] {
    return diskInfo && diskInfo.length
      ? diskInfo.map((disk) => ({
          value: `${disk.Identity}__${disk.DiskId}__${disk.WindowsVolumeIdentity}`,
          label: `${disk.LengthString} (${disk.UsedSpaceString}) ${disk.MountPointsInfo || disk.Label}`
        }))
      : [];
  }

  public static updatePartitionsStep(plan, disks: DiskVolumeView[]): PartitionsStepValue {
    const diskVolumes: any = (disks || []).filter((disk) =>
      plan.RestoreDiskConfiguration.some(
        (c) => c.SourceDiskId === disk.DiskId && c.RestoreVolumes.some((v) => v.SourceVolumeId.Identity === disk.Identity)
      )
    );
    const restoreConfigs: { [key: string]: any } = {};
    const selectedPartitionsFromRDC = [];
    plan.RestoreDiskConfiguration.forEach((rdc) =>
      rdc.RestoreVolumes.forEach((rv) => {
        restoreConfigs[`${rv.SourceVolumeId.DiskId}__${rv.SourceVolumeId.Identity}`] = rv.ExcludeRules.map((r) => {
          if (r.Folder.startsWith('\\') && r.Folder.split('\\').length === 2) r.Folder = r.Folder.substring(1);
          return r;
        });
        selectedPartitionsFromRDC.push(`${rv.SourceVolumeId.Identity}__${rv.SourceVolumeId.DiskId}`);
      })
    );
    diskVolumes.forEach((x) => (x.identityForUiField = `${x.Identity}__${x.DiskId}__${x.WindowsVolumeIdentity}`));
    return {
      selectedPartitionsFromRDC: selectedPartitionsFromRDC,
      restoreConfigs: restoreConfigs,
      restorePartitions: diskVolumes,
      restoreParentPartitions: plan.RestoreDiskConfiguration,
      convertToMBR: plan.ConvertToMBR,
      valid: true
    };
  }

  public static updateDestinationStep(
    plan,
    notIbb = true,
    volumes: DiskVolumeView[] = [],
    isUnix = false,
    isNBF = false
  ): DestinationStepValue {
    if (notIbb) {
      const result: DestinationStepValue = {
        toOriginalLocation: plan.RestoreToOriginalLocation,
        destinationFolder: !plan.RestoreToOriginalLocation ? plan.Destination : '',
        overwriteExistingFiles: !plan.SkipExistingFiles,
        restoreOnlyNewFiles: !plan.SkipExistingFiles ? plan.OnlyNewFiles : false,
        saveLocationAsDefault: false,
        valid: true
      };

      if (!isUnix) {
        result.restoreNTFSPermissions = plan.RestoreNTFSPermissions;
        result.restoreDeletedFiles = !!plan.RestoreDeletedFiles;
      } else if (isNBF) {
        result.restoreDeletedFiles = !!plan.RestoreDeletedFiles;
      }

      return result;
    } else {
      const existDisks = plan.DiskInfo && plan.DiskInfo.length;
      const foundDisk = existDisks ? plan.DiskInfo.find((disk) => disk.Enabled) : null;
      const diskId = foundDisk ? foundDisk.DiskId || plan.DiskInfo[0].DiskId : '';
      const result: DestinationStepValue = {
        destinationFolder: plan.Destination || '',
        virtualImageName: plan.RestoreDiskConfiguration[0].Name,
        overwriteExistingFiles: plan.OverwriteExisting,
        isPhysical: plan.DiskImageRestoreType !== RestoreTypeFromAgentEnum.VirtualDisk && !plan.SpecificPartitions,
        selectedDisk:
          !plan.SpecificPartitions && plan.RestoreDiskConfiguration && plan.RestoreDiskConfiguration.length
            ? plan.RestoreDiskConfiguration[0].DestinationDiskId
            : diskId,
        selectedSpecificPartitions: [],
        valid: true
      };
      if (plan.DiskImageRestoreType !== RestoreTypeFromAgentEnum.VirtualDisk && plan.SpecificPartitions) {
        const isVolumes = volumes && volumes.length;
        const RestoreVolumes: any[] = [];
        plan.RestoreDiskConfiguration.forEach((rd) => RestoreVolumes.push(...rd.RestoreVolumes));
        result.capacity = +(
          plan.RestoreDiskConfiguration[0].Capacity ? plan.RestoreDiskConfiguration[0].Capacity / 1024 / 1024 / 1024 : 0.02
        ).toFixed(2);
        result.selectedSpecificPartitions = RestoreVolumes.map((v) => {
          let volumeWhat: any = null;
          let volumeTo: any = null;
          if (isVolumes) {
            volumeTo =
              (v.DestinationVolumeId &&
                volumes.find((d) => d.DiskId === v.DestinationVolumeId.DiskId && d.Identity === v.DestinationVolumeId.Identity)) ||
              null;
            volumeWhat = volumes.find((d) => d.DiskId === v.SourceVolumeId.DiskId && d.Identity === v.SourceVolumeId.Identity) || null;
          }
          const id = `${v.SourceVolumeId.Identity}__${v.SourceVolumeId.DiskId}__${volumeWhat ? volumeWhat.WindowsVolumeIdentity : null}`;
          return volumeTo ? `${volumeTo.Identity}__${volumeTo.DiskId}__${volumeTo.WindowsVolumeIdentity}--${v.Length}--${id}` : id;
        }).filter((part) => !!part);
      }
      return result;
    }
  }

  public static updateEncryptionOptionsStep(plan, isCreate = true, isNBF = false): EncryptionOptionsStepValue {
    if (isNBF) {
      return {
        password: isCreate ? '' : plan.EncryptionPassword || '',
        valid: true
      };
    } else {
      return {
        isCryptWithPassword: !!plan.UseEncryption,
        password: isCreate ? '' : plan.EncryptionPassword || '',
        valid: true
      };
    }
  }

  public static updateRestorePointForDB(plan, offset: number): RestorePointStepValue {
    const restorePointStep: RestorePointStepValue = {
      restoreType: RestoreType.LatestVersion,
      fromDateTime: { date: '', time: '' } as SpecificDateGroup,
      toDateTime: { date: '', time: '' } as SpecificDateGroup,
      valid: true
    };

    if (plan.RestoreType === RestoreType.LatestVersion) restorePointStep.restoreType = RestoreType.LatestVersion;
    else {
      restorePointStep.restoreType = RestoreType.PointInTime;
      const dateTime = moment.utc(plan.PointInTime, ISOFormatWithSec).utcOffset(offset);
      restorePointStep.fromDateTime = {
        date: moment(dateTime.format(onlyDateFormat), onlyDateFormat).toDate(),
        time: dateTime.format('HH:mm:ss')
      };
    }

    return restorePointStep;
  }

  public static updateRestorePoint(plan, notIBB = true, restorePoint = '', offset: number, isUnix = false): RestorePointStepValue {
    const RestorePoint: RestorePointStepValue = {
      restoreType: RestoreType.LatestVersion,
      fromDateTime: { date: '', time: '' } as SpecificDateGroup,
      toDateTime: { date: '', time: '' } as SpecificDateGroup,
      valid: true
    };
    const restoreType = notIBB
      ? plan.RestoreType
      : plan.RestoreType === RestoreType.Manually ||
          plan.RestoreType === RestoreType.BackupPeriod ||
          plan.RestoreType === RestoreType.ModificationPeriod ||
          plan.RestoreType === RestoreType.PointInTime
        ? RestoreType.PointInTime
        : RestoreType.LatestVersion;

    if (restoreType === RestoreType.Manually) {
      if (plan.UseBackupDate) {
        RestorePoint.restoreType = RestoreType.BackupPeriod;
        RestorePoint.fromDateTime = SupportMethodsForStepsFromPlan.setSpecificDateGroup(true, plan.RestoreFromPointInTime, notIBB);
        RestorePoint.toDateTime = SupportMethodsForStepsFromPlan.setSpecificDateGroup(true, plan.PointInTime, notIBB);
      } else RestorePoint.restoreType = RestoreType.Manually;
    } else if (restoreType === RestoreType.LatestVersion) RestorePoint.restoreType = RestoreType.LatestVersion;
    else if (plan.RestoreFromPointInTime === MinDate || (restoreType === RestoreType.PointInTime && isNil(plan.RestoreFromPointInTime))) {
      RestorePoint.restoreType = plan.RestoreType !== RestoreType.Manually ? RestoreType.PointInTime : RestoreType.Manually;
      if (plan.IsArchive) {
        const dateTime =
          plan.PointInTime === MaxDate ? moment.utc(restorePoint, cobbledDateFormat).format(baseDateFormatWithSeconds) : plan.PointInTime;
        RestorePoint.fromDateTime = SupportMethodsForStepsFromPlan.setSpecificDateGroup(true, dateTime, notIBB || plan.IsArchive);
      } else {
        if (plan.RestoreType === RestoreType.Manually && !notIBB) {
          const dateTime = moment.utc(plan.PointInTime, ISOFormatWithSec);
          RestorePoint.manuallyDateTime = {
            date: moment(dateTime.format(onlyDateFormat), onlyDateFormat).toDate(),
            time: dateTime.format('HH:mm:ss')
          };
        } else {
          const dateTime = moment.utc(plan.PointInTime, ISOFormatWithSec).utcOffset(offset);
          RestorePoint.fromDateTime = {
            date: moment(dateTime.format(onlyDateFormat), onlyDateFormat).toDate(),
            time: dateTime.format('HH:mm:ss')
          };
        }
      }
    } else {
      RestorePoint.restoreType = plan.UseBackupDate ? RestoreType.BackupPeriod : RestoreType.ModificationPeriod;
      if (plan.IsArchive) {
        RestorePoint.fromDateTime = SupportMethodsForStepsFromPlan.setSpecificDateGroup(true, plan.RestoreFromPointInTime, notIBB);
        RestorePoint.toDateTime = SupportMethodsForStepsFromPlan.setSpecificDateGroup(true, plan.PointInTime, notIBB);
      } else {
        const fromDate = moment.utc(plan.RestoreFromPointInTime, ISOFormatWithSec).utcOffset(offset);
        const toDate = moment.utc(plan.PointInTime, ISOFormatWithSec).utcOffset(offset);
        RestorePoint.fromDateTime = {
          date: moment(fromDate.format(onlyDateFormat), onlyDateFormat).toDate(),
          time: fromDate.format('HH:mm:ss')
        };
        RestorePoint.toDateTime = { date: moment(toDate.format(onlyDateFormat), onlyDateFormat).toDate(), time: toDate.format('HH:mm:ss') };
      }
    }
    if (notIBB && isUnix) {
      RestorePoint.syncBeforeRun = plan.SyncBeforeRun;
    }
    return RestorePoint;
  }

  public static updateBackupToRestore(plan, planType: PlanTypes, name = '', isUnix = false): BackupToRestoreStepValue {
    return {
      selectedBunch: '',
      backupPlanName:
        name ||
        plan.BackupPlanName ||
        (planType === PlanTypes.RestorePlan
          ? isUnix
            ? IdsForCurrentFormats.FileUnix
            : IdsForCurrentFormats.File
          : cbfStringMap[planType]),
      valid: false
    };
  }

  public static updateRestoreSource(plan, isUnix = false, compName: string): RestoreSourceStepValue {
    const step = {
      folders: [],
      valid: true
    };
    const join = isUnix ? '/' : '\\';
    const unixOnWindowsPC =
      !isUnix && plan.Items.every((item) => windowsUpperCaseInvalidPath.test(item.Path) && !rootWinPath.test(item.Path));

    if (plan.RestoreType === RestoreType.Manually) {
      if (plan.Versions.length) {
        step.folders = [
          ...plan.Versions.map(
            (item) =>
              StepsHelpers.getRootPathWithJoin(item.Path, join, isUnix, unixOnWindowsPC ? compName : '') +
              (!item.ModifyDate || item.ModifyDate === MinDate ? '' : `${join}${StepsHelpers.getNormalDate(item.ModifyDate)}`)
          )
        ];
      }
      if (plan.Items.length) {
        step.folders = unixOnWindowsPC
          ? [...plan.Items.map((item) => StepsHelpers.getPathUnixOnWindows(item.Path, join, compName, plan.IsArchive))]
          : [...plan.Items.map((item) => StepsHelpers.getRootPathWithJoin(item.Path, join, isUnix))];
      }
    } else {
      step.folders = unixOnWindowsPC
        ? [...plan.Items.map((item) => StepsHelpers.getPathUnixOnWindows(item.Path, join, compName, plan.IsArchive))]
        : [...plan.Items.map((item) => StepsHelpers.getRootPathWithJoin(item.Path, join, isUnix))];

      if (plan.Versions.length) {
        step.folders = [
          ...step.folders,
          ...plan.Versions.map((item) => StepsHelpers.getRootPathWithJoin(item.Path, join, isUnix, unixOnWindowsPC ? compName : ''))
        ];
      }
    }
    return step;
  }

  public static updateProxmoxSource(plan: { VirtualMachines: any[] }, isUnix = false): RestoreSourceStepValue {
    const join: '/' | '\\' = isUnix ? '/' : '\\';

    return {
      folders: plan.VirtualMachines?.length
        ? plan.VirtualMachines.map(
            (machine) =>
              machine.HostName +
              join +
              `${SupportMethodsForStepsFromPlan.decodeGuidToNumber(machine.VmUUID)?.value || ''} (${machine.VMName})`
          )
        : [],
      point: '',
      valid: true
    };
  }

  public static updateVMSource(plan, isUnix = false, isCreate: boolean, type: PlanTypes): RestoreSourceStepValue {
    if (isCreate) return { folders: [], valid: true };

    const join: '/' | '\\' = isUnix ? '/' : '\\';
    const point: string =
      !plan.IsArchive && plan.RestoreType === RestoreType.Manually ? moment.utc(plan.PointInTime).format(cobbledDateFormat) : '';

    if (plan.HostType === HostTypeNumbers.HyperV || type === PlanTypes.RestoreVMwareToCustom || !plan?.VirtualMachines) {
      return { folders: [(plan.IsArchive ? plan.HostName : plan.HostName.toUpperCase()) + join + plan.VmName], point, valid: true };
    }

    return {
      folders: plan.VirtualMachines?.length
        ? plan.VirtualMachines.map(
            (machine) => (plan.IsArchive ? machine.HostName : machine.HostName.toUpperCase()) + join + machine.VMName
          )
        : [],
      point,
      valid: true
    };
  }

  public static updateDBSource(plan, isUnix = false, isCreate: boolean): RestoreSourceStepValue {
    if (isCreate) return { folders: [], valid: true };

    const join: '/' | '\\' = isUnix ? '/' : '\\';

    return {
      folders: plan.Databases?.length ? plan.Databases.map((db) => db.InstanceName + join + db.DatabaseName) : [],
      valid: true
    };
  }

  public static updateVMRestoreDisks(plan, folders: string[], isUnix = false): RestoreDisksStepValue {
    const join = isUnix ? '/' : '\\';
    const stepData: RestoreDisksStepValue = { type: VirtualDisksSelectedType.All, folders: folders || [], excludes: [], valid: true };

    plan.VirtualMachines?.forEach((machine) => {
      machine.ExcludeDisks.forEach((exc: string) => {
        stepData.excludes.push(machine.HostName + join + machine.VMName + join + exc);
      });
    });

    if (stepData.excludes.length) stepData.type = VirtualDisksSelectedType.Selected;

    return stepData;
  }

  public static updateVMDestinationStep(plan, planType: PlanTypes, isUnix: boolean): DestinationVMStepValue {
    const stepData: DestinationVMStepValue = { destinationFolder: plan.RestorePath || plan.Destination, specificList: [], valid: false };

    if (planType === PlanTypes.RestoreProxmox) {
      stepData.overwriteExistingVM = plan.Overwrite;

      const join = isUnix ? '/' : '\\';
      stepData.specificList = plan.VirtualMachines?.map((machine) => {
        const newName = machine.NewVMName || machine.VMName;
        const restoreAs = machine.NewVmUUID
          ? `${SupportMethodsForStepsFromPlan.decodeGuidToNumber(machine.NewVmUUID)?.value || ''} (${newName})`
          : newName;
        return `${machine.HostName}${join}${SupportMethodsForStepsFromPlan.decodeGuidToNumber(machine.VmUUID)?.value || ''} (${machine.VMName})--${restoreAs}`;
      });
    }

    if (plan.RestorePlanType === RestorePlanType.HyperV || plan.RestorePlanType === RestorePlanType.VMwareMachine) {
      stepData.overwriteExistingVM = plan.Overwrite;

      const join = isUnix ? '/' : '\\';
      stepData.specificList = plan.VirtualMachines?.map(
        (machine) => `${machine.HostName}${join}${machine.VMName}--${machine.NewVMName || machine.VMName}`
      );
    }

    if (planType === PlanTypes.RestoreProxmox || planType === PlanTypes.RestoreVMware) {
      stepData.destinationFolder = plan.Destination;
      stepData.dataStore = plan.Datastore;
      stepData.powerOnVM = plan.PowerOnAfterRestore;

      return stepData;
    }

    stepData.powerOnVM = plan.PowerOnAfterRestore;
    stepData.toOriginalLocation = plan.RestoreToOriginalLocation;

    return stepData;
  }

  public static updateDBDestinationFileStep(plan): DestinationDBStepValue {
    return {
      destinationFolder: plan.Destination,
      createFolder: plan.CreateFolderForEachDatabase,
      backupTemplate: plan.FileNameTemplate || DefaultTemplate,
      valid: false
    };
  }

  public static updateDBDestinationStep(plan, isUnix = false): DestinationDBStepValue {
    const stepData: DestinationDBStepValue = {
      destinationFolder: plan.DataPath,
      destinationFolderLog: plan.LogPath,
      closeConnection: plan.CloseConnection,
      backupTemplate: plan.FileMask || DefaultTemplateMask,
      overwriteExisting: plan.Overwrite,
      specificList: [],
      valid: false
    };

    const join = isUnix ? '/' : '\\';
    stepData.specificList = plan.Databases?.map(
      (db) => `${db.InstanceName}${join}${db.DatabaseName}--${db.NewDatabaseName || db.DatabaseName}`
    );

    return stepData;
  }

  private static updateTypeDataEC2(EC2RestoreType: EC2RestoreTypeEnum, typeData: TypeDataStepValue): void {
    if (EC2RestoreType === EC2RestoreTypeEnum.EC2Instance) typeData.restoreAs = RestoreAsDiskEnum.EC2;
    else if (EC2RestoreType === EC2RestoreTypeEnum.EC2Volume) typeData.restoreAs = RestoreAsDiskEnum.EBSVolume;
    else if (EC2RestoreType === EC2RestoreTypeEnum.EC2Image) {
      typeData.restoreAs = RestoreAsDiskEnum.EC2;
      typeData.createAMI = true;
    }
  }
}
